var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { MAPFlowTypes } from 'make-a-payment';
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { settlementEligibilityCheckUrl } from '../const/api-endpoints';
import { CAPAutopayStatuses, Flows, Routes } from '../const';
import { useMainContext } from '../context';
import { enrollPlan, enrollSettlement } from '../helpers/enrollment';
import { getButtonAnalytics } from '../helpers/getAnalytics';
import { submitQuestionnaire } from '../helpers/questionnaire';
import usePlanEnrollment from './use-plan-eligibility-enrollment';
import useTranslation from './use-translation';
var usePrimaryAction = function (flow, actions) {
    var _a;
    var handleCloseModal = actions.handleCloseModal, navigateTo = actions.navigateTo, onTransitionEnd = actions.onTransitionEnd, setModal = actions.setModal;
    var _b = useMainContext(), currentRoute = _b.currentRoute, setCurrentRoute = _b.setCurrentRoute, accountId = _b.accountId, accountIdType = _b.accountIdType, autopayStatus = _b.autopayStatus;
    var _c = useState(false), disablePrimary = _c[0], setDisablePrimary = _c[1];
    var i18n = useTranslation();
    var planEnroll = usePlanEnrollment();
    var queryClient = useQueryClient();
    var survey = flow === null || flow === void 0 ? void 0 : flow.questionnaire;
    var index = survey === null || survey === void 0 ? void 0 : survey.index;
    var surveyLength = (_a = survey === null || survey === void 0 ? void 0 : survey.questions) === null || _a === void 0 ? void 0 : _a.length;
    var hasNextQuestion = index < surveyLength - 1;
    var contactRoute = currentRoute === Routes.PLAN_CONTACT ||
        currentRoute === Routes.SETTLEMENT_CONTACT;
    var buttonAnalytics = getButtonAnalytics(currentRoute, contactRoute);
    var handleContinue = function () {
        switch (currentRoute) {
            case Routes.SETTLEMENT_START:
                setCurrentRoute(Routes.SETTLEMENT_QUESTIONNAIRE);
                return;
            case Routes.SETTLEMENT_QUESTIONNAIRE:
                {
                    flow.setQuestionIndex(index + 1);
                    var allQuestionsAnswered = survey.selectedAnswers.length === survey.questions.length;
                    var firstQuestion = survey.selectedAnswers.length - 1;
                    if (allQuestionsAnswered && index === survey.questions.length - 1) {
                        flow.setQuestionIndex(firstQuestion);
                        submitQuestionnaire({
                            flow: flow,
                            currentFlow: Flows.SETTLEMENT,
                            setDisablePrimary: setDisablePrimary,
                            setCurrentRoute: setCurrentRoute
                        });
                    }
                }
                return;
            case Routes.SETTLEMENT_OPTIONS:
                setCurrentRoute(Routes.SETTLEMENT_REVIEW);
                return;
            case Routes.SETTLEMENT_REVIEW:
                enrollSettlement(flow, setDisablePrimary, setCurrentRoute);
                return;
            case Routes.SETTLEMENT_CONTACT:
            case Routes.PLAN_CONTACT:
                onTransitionEnd();
                return;
            case Routes.PLAN_START:
                setCurrentRoute(Routes.PLAN_QUESTIONNAIRE);
                return;
            case Routes.PLAN_QUESTIONNAIRE:
                {
                    flow.setQuestionIndex(index + 1);
                    var allQuestionsAnswered = survey.selectedAnswers.length === survey.questions.length;
                    var firstQuestion = survey.selectedAnswers.length - 1;
                    if (allQuestionsAnswered && index === survey.questions.length - 1) {
                        flow.setQuestionIndex(firstQuestion);
                        submitQuestionnaire({
                            flow: flow,
                            currentFlow: Flows.PLAN,
                            setDisablePrimary: setDisablePrimary,
                            setCurrentRoute: setCurrentRoute
                        });
                    }
                }
                return;
            case Routes.PLAN_REVIEW:
                setCurrentRoute(Routes.PLAN_CONFIRM);
                return;
            case Routes.PLAN_CONFIRM:
                enrollPlan({
                    currentFlow: Flows.PLAN,
                    currentRoute: currentRoute,
                    flow: flow,
                    planEnroll: planEnroll,
                    setCurrentRoute: setCurrentRoute,
                    setDisablePrimary: setDisablePrimary
                });
                break;
            case Routes.PLAN_FORBEARANCE:
                setCurrentRoute(Routes.PAYMENT_OPTIONS);
                break;
            default:
        }
    };
    var closeButtonText = i18n('questionnaireFooter.closeButton');
    return function () {
        var _a;
        var questionnairePrimaryText = hasNextQuestion
            ? 'nextQuestionButton'
            : 'submitQuestionnaireButton';
        var questionsAnswered = index === survey.selectedAnswers.length;
        var isPrimaryDisabled = questionsAnswered || disablePrimary;
        var continueButton = 'questionnaireFooter.continueButton';
        var buttonAnalyticsAction = hasNextQuestion
            ? 'next question'
            : 'submit questionnaire';
        switch (currentRoute) {
            case Routes.SETTLEMENT_START:
            case Routes.PLAN_START:
                return __assign(__assign({ text: i18n('questionnaireFooter.startButton'), action: handleContinue, isDisabled: !((_a = flow === null || flow === void 0 ? void 0 : flow.questionnaire) === null || _a === void 0 ? void 0 : _a.questions) }, buttonAnalytics), { dataReason: 'lets get started' });
            case Routes.SETTLEMENT_QUESTIONNAIRE:
            case Routes.PLAN_QUESTIONNAIRE:
                return __assign(__assign({ text: i18n("questionnaireFooter.".concat(questionnairePrimaryText)), isDisabled: isPrimaryDisabled, action: handleContinue }, buttonAnalytics), { dataReason: buttonAnalyticsAction });
            case Routes.SETTLEMENT_OPTIONS:
                return __assign(__assign({ text: i18n(continueButton), action: handleContinue, isDisabled: !(flow === null || flow === void 0 ? void 0 : flow.plan) }, buttonAnalytics), { dataReason: 'continue' });
            case Routes.SETTLEMENT_REVIEW:
                return __assign(__assign({ text: i18n('questionnaireFooter.settlementReviewContinueButton'), isDisabled: isPrimaryDisabled, action: handleContinue }, buttonAnalytics), { dataReason: 'accept terms and continue' });
            case Routes.PLAN_REVIEW:
                return __assign(__assign({ text: i18n('questionnaireFooter.capReviewContinueButton'), isDisabled: isPrimaryDisabled, action: handleContinue }, buttonAnalytics), { dataReason: 'accept terms and continue' });
            case Routes.SETTLEMENT_SUCCESS:
                return __assign(__assign({ text: i18n('paymentOptions.oneTimePaymentCardButton'), action: function () {
                        var _a;
                        var mapFlow = (((_a = flow.plan.installments) === null || _a === void 0 ? void 0 : _a.length) || 0) > 1
                            ? MAPFlowTypes.MULTI_PAYMENT_FLOW
                            : MAPFlowTypes.ONE_TIME_PAYMENT_FLOW;
                        queryClient.invalidateQueries({
                            queryKey: [
                                settlementEligibilityCheckUrl,
                                { accountId: accountId, accountIdType: accountIdType }
                            ]
                        });
                        navigateTo('payment', mapFlow);
                    } }, buttonAnalytics), { dataReason: 'make a payment' });
            case Routes.SETTLEMENT_ERROR:
            case Routes.PLAN_ERROR:
                return __assign(__assign({ text: closeButtonText, action: handleCloseModal }, buttonAnalytics), { dataReason: 'close' });
            case Routes.PLAN_CONTACT:
            case Routes.SETTLEMENT_CONTACT:
                return __assign(__assign({ text: i18n('paymentOptions.modalHeading'), action: handleContinue }, buttonAnalytics), { dataReason: 'payment options' });
            case Routes.PLAN_CONFIRM:
                return __assign(__assign({ text: i18n('plan.confirmContinueButton'), isDisabled: isPrimaryDisabled, action: handleContinue }, buttonAnalytics), { dataReason: i18n('plan.confirmContinueButton') });
            case Routes.PLAN_FORBEARANCE:
                return __assign(__assign({ text: i18n('plan.otherPaymentOptionsButton'), action: handleContinue }, buttonAnalytics), { dataReason: i18n('plan.otherPaymentOptionsButton') });
            case Routes.PLAN_SUCCESS:
                // enroll in autopay - analytics
                if (autopayStatus === CAPAutopayStatuses.ERROR) {
                    return __assign(__assign({ text: closeButtonText, action: handleCloseModal }, buttonAnalytics), { dataReason: 'close' });
                }
                return __assign(__assign({ text: i18n('plan.enrollAutopayButton') }, buttonAnalytics), { dataReason: i18n('plan.enrollAutopayButton'), dataType: 'dch cap footer', action: function () {
                        return setModal('MAP', '', {
                            MAP: {
                                flow: {
                                    type: MAPFlowTypes.AUTOPAY_FLOW
                                }
                            }
                        });
                    } });
            default:
                return null;
        }
    };
};
export default usePrimaryAction;
//# sourceMappingURL=use-primary-action.js.map